// src/pages/Admin/Adherents/components/CreateAdherentDrawer.jsx

import React, { useCallback, useEffect, useState } from "react";
import { FaSave, FaTimes, FaUserPlus } from "react-icons/fa";
import { toast } from "react-toastify";

import AdherentDrawer from "./AdherentDrawer";

import { addNewAdherent } from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";
import { createDossierInscription } from "../../../../../services/dossierServices";

const initialState = {
  nom: "",
  prenom: "",
  email: "",
  email_secondaire: "",
  tel: "",
  tel_secondaire: "",
  representant_legal: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "",
  statut_inscription: "Inscrit",
  date_naissance: "",
  lieu_naissance: "",
  photo_url: "",
  licence_number: "",
  poids: "",
  saison_active: "",
  notes_adherent: "",
  autorisation_photo: false,
  passsport_number: "",
};

function getPreviousSeason(currentSeason = "") {
  const [y1, y2] = String(currentSeason).split("-").map(Number);
  if (!y1 || !y2) return "";
  return `${y1 - 1}-${y2 - 1}`;
}

function getNextSeason(currentSeason = "") {
  const [y1, y2] = String(currentSeason).split("-").map(Number);
  if (!y1 || !y2) return "";
  return `${y1 + 1}-${y2 + 1}`;
}

function FieldLabel({ children }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
    </label>
  );
}

function SectionCard({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
          {subtitle ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  );
}

export default function CreateAdherentDrawer({ isOpen, onClose, refreshList }) {
  const [formData, setFormData] = useState(initialState);
  const [seasons, setSeasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  const loadSeasons = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      const settings = Array.isArray(settingsData) ? settingsData[0] : settingsData;
      const active = settings?.saison_active || "";

      const options = [
        getPreviousSeason(active),
        active,
        getNextSeason(active),
      ].filter(Boolean);

      setSeasons(options);

      setFormData((prev) => ({
        ...prev,
        saison_active: prev.saison_active || active || "",
      }));
    } catch (error) {
      console.error("Erreur chargement saisons :", error);
      toast.error("Erreur lors du chargement des saisons.");
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialState);
    setImgOk(true);
    setSaving(false);
    loadSeasons();
  }, [isOpen, loadSeasons]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "photo_url") {
      setImgOk(true);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const canSubmit =
    formData.nom.trim() !== "" &&
    formData.prenom.trim() !== "" &&
    String(formData.saison_active || "").trim() !== "" &&
    !saving;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    try {
      setSaving(true);

      const newAdherent = await addNewAdherent(formData);

      if (!newAdherent?.id) {
        throw new Error("Création adhérent : aucun ID retourné.");
      }

      await createDossierInscription(newAdherent.id);

      toast.success("Adhérent et dossier d'inscription créés avec succès.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur création adhérent :", error);
      toast.error("Erreur lors de la création de l'adhérent.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdherentDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Créer un adhérent"
      subtitle="Ajoutez un nouvel adhérent et créez automatiquement son dossier d'inscription."
      width="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <SectionCard
            icon={<FaUserPlus />}
            title="État civil"
            subtitle="Les champs Nom, Prénom et Saison sont obligatoires."
          >
            <div className="grid gap-5 lg:grid-cols-[1fr_260px]">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel>Nom *</FieldLabel>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Prénom *</FieldLabel>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Genre</FieldLabel>
                  <select
                    name="genre"
                    value={formData.genre}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="Masculin">Masculin</option>
                    <option value="Féminin">Féminin</option>
                  </select>
                </div>

                <div>
                  <FieldLabel>Date de naissance</FieldLabel>
                  <input
                    type="date"
                    name="date_naissance"
                    value={formData.date_naissance}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Lieu de naissance</FieldLabel>
                  <input
                    type="text"
                    name="lieu_naissance"
                    value={formData.lieu_naissance}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Numéro de licence</FieldLabel>
                  <input
                    type="text"
                    name="licence_number"
                    value={formData.licence_number}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Poids kg</FieldLabel>
                  <input
                    type="number"
                    step="0.1"
                    name="poids"
                    value={formData.poids}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  Photo
                </p>

                <div className="mt-3 aspect-square overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900">
                  {formData.photo_url ? (
                    <img
                      src={formData.photo_url}
                      alt="Aperçu adhérent"
                      onError={() => setImgOk(false)}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center px-4 text-center text-xs text-slate-500 dark:text-slate-400">
                      Aucun visuel
                    </div>
                  )}
                </div>

                {!imgOk ? (
                  <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                    Impossible de charger l'image.
                  </p>
                ) : null}

                <div className="mt-3">
                  <FieldLabel>URL photo</FieldLabel>
                  <input
                    type="text"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    placeholder="https://... ou /uploads/..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </div>

                <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  L'upload et le recadrage seront disponibles après création depuis
                  le panneau d'édition.
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaUserPlus />}
            title="Coordonnées"
            subtitle="Informations de contact de l'adhérent ou du représentant légal."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Email</FieldLabel>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Email secondaire</FieldLabel>
                <input
                  type="email"
                  name="email_secondaire"
                  value={formData.email_secondaire}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Téléphone</FieldLabel>
                <input
                  type="text"
                  name="tel"
                  value={formData.tel}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Téléphone secondaire</FieldLabel>
                <input
                  type="text"
                  name="tel_secondaire"
                  value={formData.tel_secondaire}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <FieldLabel>Représentant légal</FieldLabel>
                <input
                  type="text"
                  name="representant_legal"
                  value={formData.representant_legal}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <FieldLabel>Adresse</FieldLabel>
                <input
                  type="text"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Code postal</FieldLabel>
                <input
                  type="text"
                  name="cp"
                  value={formData.cp}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Commune</FieldLabel>
                <input
                  type="text"
                  name="commune"
                  value={formData.commune}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaUserPlus />}
            title="Infos club"
            subtitle="Statut d'inscription, saison active, Pass'Sport et notes internes."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Statut d'inscription</FieldLabel>
                <select
                  name="statut_inscription"
                  value={formData.statut_inscription}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="Inscrit">Inscrit</option>
                  <option value="Licencié">Licencié</option>
                  <option value="Cours d'essai">Cours d'essai</option>
                  <option value="Licencié - Arrêt">Licencié - Arrêt</option>
                  <option value="Non Licencié - Arrêt">Non Licencié - Arrêt</option>
                  <option value="Licencié Hors Club">Licencié Hors Club</option>
                  <option value="Ancien Licencié">Ancien Licencié</option>
                  <option value="Ancien Licencié et Arrêt">
                    Ancien Licencié et Arrêt
                  </option>
                </select>
              </div>

              <div>
                <FieldLabel>Saison active *</FieldLabel>
                <select
                  name="saison_active"
                  value={formData.saison_active}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionner —</option>
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FieldLabel>N° Pass'Sport</FieldLabel>
                <input
                  type="text"
                  name="passsport_number"
                  value={formData.passsport_number}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="flex items-end">
                <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                  <input
                    type="checkbox"
                    name="autorisation_photo"
                    checked={formData.autorisation_photo}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                  />
                  Autorisation photo
                </label>
              </div>

              <div className="sm:col-span-2">
                <FieldLabel>Notes</FieldLabel>
                <textarea
                  name="notes_adherent"
                  value={formData.notes_adherent}
                  onChange={handleChange}
                  rows={4}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Création..." : "Créer"}
          </button>
        </div>
      </form>
    </AdherentDrawer>
  );
}